/** Identifiants d'édition ITaaS Edu (gamme type Microsoft 365 / HubSpot). */
export type EduEditionId = "essential" | "standard" | "premium" | "enterprise";

/** Fonctionnalités contrôlées par édition (au-delà du socle Essential). */
export type EduEditionFeature =
  | "portal_parents"
  | "portal_students"
  | "timetable"
  | "documents"
  | "notifications"
  | "agenda"
  | "exams"
  | "advanced_stats"
  | "hr"
  | "personnel"
  | "contracts"
  | "leave"
  | "evaluations"
  | "discipline"
  | "library"
  | "inventory"
  | "multi_campus"
  | "bi"
  | "multi_establishments"
  | "api_rest"
  | "sso"
  | "custom_domain";

export interface EduEditionDefinition {
  id: EduEditionId;
  displayName: string;
  tagline: string;
  monthlyUsd: number | null;
  maxStudents: number | null;
  /** null = illimité / sur devis */
  maxStudentsLabel: string;
  highlight?: boolean;
  profiles: string[];
  modules: string[];
  newInEdition?: string[];
}

export const EDU_EDITION_ORDER: readonly EduEditionId[] = [
  "essential",
  "standard",
  "premium",
  "enterprise",
] as const;

export const EDU_EDITIONS: Record<EduEditionId, EduEditionDefinition> = {
  essential: {
    id: "essential",
    displayName: "ITaaS Edu Essential",
    tagline: "Pour les petites écoles",
    monthlyUsd: 150,
    maxStudents: 200,
    maxStudentsLabel: "Jusqu'à 200 élèves",
    profiles: ["Promoteur", "Directeur", "Secrétaire", "Enseignants", "Comptable"],
    modules: [
      "Gestion des élèves",
      "Inscriptions",
      "Classes",
      "Enseignants",
      "Matières",
      "Présences",
      "Notes",
      "Bulletins",
      "Paiements",
      "Tableau de bord",
      "Rapports",
    ],
  },
  standard: {
    id: "standard",
    displayName: "ITaaS Edu Standard",
    tagline: "Pour les écoles en croissance",
    monthlyUsd: 300,
    maxStudents: 500,
    maxStudentsLabel: "Jusqu'à 500 élèves",
    highlight: true,
    profiles: ["Parents", "Élèves"],
    modules: [
      "Portail Parents",
      "Portail Élèves",
      "Emplois du temps",
      "Documents",
      "Notifications",
      "Agenda",
      "Examens",
      "Statistiques avancées",
    ],
    newInEdition: [
      "Portail Parents",
      "Portail Élèves",
      "Emplois du temps",
      "Documents",
      "Notifications",
      "Agenda",
      "Examens",
      "Statistiques avancées",
    ],
  },
  premium: {
    id: "premium",
    displayName: "ITaaS Edu Premium",
    tagline: "Pour les établissements exigeants",
    monthlyUsd: 525,
    maxStudents: 1000,
    maxStudentsLabel: "Jusqu'à 1 000 élèves",
    profiles: ["Responsable RH", "Responsable informatique"],
    modules: [
      "Ressources Humaines",
      "Gestion du personnel",
      "Contrats",
      "Congés",
      "Évaluations",
      "Discipline",
      "Bibliothèque numérique",
      "Inventaire",
      "Multi-campus",
      "Business Intelligence",
    ],
    newInEdition: [
      "Ressources Humaines",
      "Gestion du personnel",
      "Discipline",
      "Bibliothèque numérique",
      "Inventaire",
      "Multi-campus",
      "Business Intelligence",
    ],
  },
  enterprise: {
    id: "enterprise",
    displayName: "ITaaS Edu Enterprise",
    tagline: "Pour les grands groupes scolaires",
    monthlyUsd: 900,
    maxStudents: null,
    maxStudentsLabel: "Plus de 1 000 élèves",
    profiles: [],
    modules: [
      "Multi-établissements",
      "API REST",
      "SSO",
      "Domaine personnalisé",
      "Hébergement dédié",
      "Sauvegardes dédiées",
      "SLA Premium",
      "Accompagnement personnalisé",
    ],
    newInEdition: [
      "Multi-établissements",
      "API REST",
      "SSO",
      "Domaine personnalisé",
      "SLA Premium",
    ],
  },
};

/** Édition minimale requise pour chaque fonctionnalité. */
export const EDU_FEATURE_MIN_EDITION: Record<EduEditionFeature, EduEditionId> = {
  portal_parents: "standard",
  portal_students: "standard",
  timetable: "standard",
  documents: "standard",
  notifications: "standard",
  agenda: "standard",
  exams: "standard",
  advanced_stats: "standard",
  hr: "premium",
  personnel: "premium",
  contracts: "premium",
  leave: "premium",
  evaluations: "premium",
  discipline: "premium",
  library: "premium",
  inventory: "premium",
  multi_campus: "premium",
  bi: "premium",
  multi_establishments: "enterprise",
  api_rest: "enterprise",
  sso: "enterprise",
  custom_domain: "enterprise",
};

export function normalizeEduEdition(value: string | null | undefined): EduEditionId {
  const v = value?.trim().toLowerCase();
  if (v === "standard" || v === "premium" || v === "enterprise") return v;
  if (v === "starter" || v === "professional") return "standard";
  return "essential";
}

export function editionTierIndex(edition: EduEditionId): number {
  return EDU_EDITION_ORDER.indexOf(edition);
}

export function minimumEditionForFeature(feature: EduEditionFeature): EduEditionId {
  return EDU_FEATURE_MIN_EDITION[feature];
}

export function editionIncludesFeature(
  edition: EduEditionId,
  feature: EduEditionFeature,
): boolean {
  return editionTierIndex(edition) >= editionTierIndex(EDU_FEATURE_MIN_EDITION[feature]);
}

export function editionDisplayName(edition: EduEditionId): string {
  return EDU_EDITIONS[edition].displayName;
}

export function listEditionsUpTo(edition: EduEditionId): EduEditionId[] {
  const idx = editionTierIndex(edition);
  return EDU_EDITION_ORDER.slice(0, idx + 1) as EduEditionId[];
}

export function upgradeLabelForFeature(feature: EduEditionFeature): string {
  const min = EDU_EDITIONS[EDU_FEATURE_MIN_EDITION[feature]];
  return min.displayName.replace("ITaaS Edu ", "");
}
